import React from "react";
import { Grid, Typography, Card, Box } from "@material-ui/core";
import DashboardIcon from "@material-ui/icons/Dashboard";

import translate from "../i18n/Translator";
import Surface from "../components/Surface";
import { CurrencyCollectionResponse, CollectionPerformanceResponse } from "../model/Dashboard";
import CurrencyFormat from "../components/CurrencyFormat";
import NumberFormat from "react-number-format";


interface CollectionWidgetProps {
    collection: CurrencyCollectionResponse[];
}

interface CollectionDashboardProps extends CollectionWidgetProps {
    collection_performance: CollectionPerformanceResponse[];
    dollar?: number;
    euro?: number;
    activeCustomers?: number;
}

export default function CollectionDashboard(props: CollectionDashboardProps) {

    return (
        <Surface title={translate("dashboard.collection.title")} icon={<DashboardIcon />} titleActions={props.activeCustomers === undefined ? undefined : (
            <>
                <Typography variant="body2" component="h6" align="right">
                    {translate("dashboard.collection.customers")}
                </Typography>
                <Typography variant="subtitle2" component="h6" align="right">
                    <strong>
                        <NumberFormat value={props.activeCustomers}
                            suffix=" %"
                            decimalScale={2}
                            decimalSeparator="."
                            thousandSeparator=","
                            fixedDecimalScale={true}
                            displayType="text" />
                    </strong>
                </Typography>
            </>
        )}>
            <Grid container spacing={3}>
                <Grid item xs={12} lg={6} xl={6}>
                    <Grid container>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" component="h6">
                                {translate("dashboard.collection.currencies")}
                            </Typography>
                        </Grid>
                    </Grid>
                    <CollectionWidget {...props} />
                </Grid>
                <Grid item xs={12} sm={8} lg={3} xl={4}>
                    <Grid container>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" component="h6">
                                {translate("dashboard.collection_performance.title")}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        {props.collection_performance.map(item => (
                            <Grid item xs={12} key={item.currency}>
                                <Card variant="outlined">
                                    <Box py={1}>
                                        <Grid container>
                                            <Grid item xs={12}>
                                                <Typography variant="button" component="h6" align="center">
                                                    {item.currency} - {translate(`currencies.${item.currency}`)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" component="h6" align="center">
                                                    <CurrencyFormat amount={item.balance} currency={item.currency} />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" component="h6" align="center">
                                                    <strong>
                                                        <NumberFormat value={item.performance * 100}
                                                            prefix="%"
                                                            decimalScale={2}
                                                            decimalSeparator="."
                                                            thousandSeparator=","
                                                            fixedDecimalScale={true}
                                                            displayType="text" />
                                                    </strong>
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={4} lg={3} xl={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" component="h6">
                                {translate("dashboard.exchange_rates.title")}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">
                                {translate("currencies.USD")}
                            </Typography>
                            {props.dollar && (
                                <Typography variant="h5">
                                    <CurrencyFormat amount={props.dollar} currency="MXN" decimals={4} />
                                </Typography>
                            )}
                            {!props.dollar && (
                                <Typography variant="h6" color="error">
                                    {translate("dashboard.exchange_rates.not_available")}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">
                                {translate("currencies.EUR")}
                            </Typography>
                            {props.euro && (
                                <Typography variant="h5">
                                    <CurrencyFormat amount={props.euro} currency="MXN" decimals={4} />
                                </Typography>
                            )}
                            {!props.euro && (
                                <Typography variant="h6" color="error">
                                    {translate("dashboard.exchange_rates.not_available")}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Surface>
    );
}

export function CollectionWidget(props: CollectionWidgetProps) {
    return (
        <Grid container spacing={2}>
            {props.collection.map(item => (
                <Grid item xs={12} key={item.currency}>
                    <Card variant="outlined">
                        <Box py={1}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <Typography variant="button" component="h6" align="center">
                                        {item.currency} - {translate(`currencies.${item.currency}`)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container>
                                        {item.items.map(segment => (
                                            <Grid item key={segment.segment} xs={6} md={3}>
                                                <Typography variant="caption" component="h6" align="center">
                                                    {translate(`dashboard.collection.segments.${segment.segment}`)}
                                                </Typography>
                                                <Typography variant="body2" component="h6" align="center">
                                                    <CurrencyFormat amount={segment.balance} currency={item.currency} />
                                                </Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
}